import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPhoneGroups, getTemplate, getGroupPhoneNumbersAll, getFlowsByWabaId, getFlowById, refreshToken } from '../../../api/login.service';

// Flow metadata interface
interface FlowMetadata {
  id: string;
  name: string;
  wabaId: string;
  nodeCount: number;
  edgeCount: number;
  lastModified: number;
  createdAt: number;
  nodeTypes?: string[]; // Types of nodes in the flow
}

// Lightweight date formatter (matches templates list style)
function formatDate(timestamp: number) {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function AutomatedFlowsList() {
  const navigate = useNavigate();
  const wabaId = sessionStorage.getItem('wabaId');
  
  const [flows, setFlows] = useState<FlowMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingFlowId, setDeletingFlowId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [previewFlowId, setPreviewFlowId] = useState<string | null>(null);
  const [previewFlowData, setPreviewFlowData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'flow' | 'bot' | 'numbers'>('flow');
  const [phoneGroups, setPhoneGroups] = useState<any[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [botSettings, setBotSettings] = useState({
    enabled: false,
    name: '',
    description: '',
  });
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [templateDetails, setTemplateDetails] = useState<Record<string, any>>({});
  const [recipientPreviews, setRecipientPreviews] = useState<any[]>([]);
  const [loadingPreviews, setLoadingPreviews] = useState(false);

  // Load all flows from API
  const loadFlows = useCallback(async () => {
    if (!wabaId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Refresh token before fetching flows
      console.log('Refreshing token before loading flows...');
      const newToken = await refreshToken();
      if (!newToken) {
        console.warn('Token refresh failed or no refresh token available, proceeding with current token');
      } else {
        console.log('Token refreshed successfully');
      }
      
      // Fetch flows from API
      const response = await getFlowsByWabaId(wabaId);
      
      console.log('loadFlows - Full API response:', JSON.stringify(response, null, 2));
      
      // Handle response structure: { success: true, data: { flows: [...], count: number } }
      // getFlowsByWabaId returns response.data, so response is already { success: true, data: { flows: [...] } }
      let flowsData = [];
      
      if (response?.data?.flows && Array.isArray(response.data.flows)) {
        flowsData = response.data.flows;
      } else if (response?.flows && Array.isArray(response.flows)) {
        flowsData = response.flows;
      } else if (Array.isArray(response)) {
        flowsData = response;
      }
      
      console.log('loadFlows - Extracted flowsData:', flowsData);
      console.log('loadFlows - flowsData length:', flowsData.length);
      console.log('loadFlows - flowsData is array:', Array.isArray(flowsData));
      
      if (!Array.isArray(flowsData) || flowsData.length === 0) {
        console.warn('No flows found in response. Response structure:', response);
        setFlows([]);
        return;
      }
      
      const allFlows: FlowMetadata[] = flowsData.map((flow: any) => {
        // Extract flow_settings array from the flow data
        const flowSettings = flow.flow_settings || [];
        
        // Calculate node count (excluding config node)
        const nodeCount = flowSettings.filter((node: any) => node.kind !== 'config').length;
        
        // Calculate edge count
        const edgeCount = flowSettings.reduce((acc: number, node: any) => {
          return acc + (node.transitions ? Object.keys(node.transitions).length : 0);
        }, 0);
        
        // Extract node types (excluding config)
        const nodeTypes = flowSettings
          .filter((node: any) => node.kind !== 'config')
          .map((node: any) => String(node.kind || 'unknown'))
          .filter((kind: string) => kind !== 'unknown');
        const uniqueNodeTypes: string[] = Array.from(new Set(nodeTypes)) as string[];
        
        // Get metadata from config object or flow properties
        const configNode = flowSettings.find((node: any) => node.kind === 'config');
        const metadata = configNode?.config?.metadata || {};
        
        // Parse dates
        const createdAt = flow.createdAt 
          ? new Date(flow.createdAt).getTime() 
          : (metadata.createdAt || Date.now());
        const updatedAt = flow.updatedAt 
          ? new Date(flow.updatedAt).getTime() 
          : (metadata.lastModified || Date.now());
        
        return {
          id: flow.flowId || flow.id || flow._id || 'unknown',
          name: flow.flowName || metadata.name || flow.name || 'Unnamed Flow',
          wabaId: flow.wabaId || wabaId,
          nodeCount,
          edgeCount,
          lastModified: updatedAt,
          createdAt: createdAt,
          nodeTypes: uniqueNodeTypes,
        };
      });
      
      // Sort by last modified (newest first)
      allFlows.sort((a, b) => b.lastModified - a.lastModified);
      
      setFlows(allFlows);
    } catch (e: any) {
      console.error('Error loading flows:', e);
      setError(e?.message || 'Failed to load flows');
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, [wabaId]);

  // Load flows on mount and when wabaId changes
  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const handleDelete = async (flowId: string) => {
    if (!wabaId) return;
    
    setDeletingFlowId(flowId);
    setError(null);
    try {
      // Delete flow data
      const flowKey = flowId === 'default' 
        ? `automated_flow_${wabaId}`
        : `automated_flow_${wabaId}_${flowId}`;
      localStorage.removeItem(flowKey);
      
      // Delete metadata
      const metadataKey = `flow_metadata_${wabaId}_${flowId}`;
      localStorage.removeItem(metadataKey);
      
      // Reload flows
      loadFlows();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete flow');
    } finally {
      setDeletingFlowId(null);
      setShowDeleteConfirm(null);
    }
  };

  const handleEdit = async (flowId: string) => {
    if (!wabaId) return;
    
    try {
      // Refresh token before fetching flow
      console.log('Refreshing token before editing flow...');
      const newToken = await refreshToken();
      if (!newToken) {
        console.warn('Token refresh failed or no refresh token available, proceeding with current token');
      } else {
        console.log('Token refreshed successfully');
      }
      
      // Fetch flow data from API
      const response = await getFlowById(flowId);
      const flowData = response?.data || response;
      
      if (flowData) {
        // Store flow data in sessionStorage for the builder to load
        sessionStorage.setItem('editingFlowId', flowId);
        sessionStorage.setItem('editingFlowData', JSON.stringify(flowData));
        navigate('/events/automated-flow-builder');
      } else {
        setError('Flow not found');
      }
    } catch (e: any) {
      console.error('Error loading flow for editing:', e);
      setError(e?.message || 'Failed to load flow for editing');
    }
  };

  const handleCreate = () => {
    // Clear any editing flow ID
    sessionStorage.removeItem('editingFlowId');
    navigate('/events/automated-flow-builder');
  };

  const handleDuplicate = (flowId: string) => {
    if (!wabaId) return;
    
    try {
      const flowKey = flowId === 'default' 
        ? `automated_flow_${wabaId}`
        : `automated_flow_${wabaId}_${flowId}`;
      
      const flowData = localStorage.getItem(flowKey);
      if (!flowData) {
        setError('Flow not found');
        return;
      }
      
      // Create new flow ID
      const newFlowId = `flow_${Date.now()}`;
      const newFlowKey = `automated_flow_${wabaId}_${newFlowId}`;
      
      // Copy flow data
      localStorage.setItem(newFlowKey, flowData);
      
      // Copy and update metadata
      const metadataKey = `flow_metadata_${wabaId}_${flowId}`;
      const existingMetadata = localStorage.getItem(metadataKey);
      const newMetadata = {
        name: existingMetadata 
          ? `${JSON.parse(existingMetadata).name} (Copy)`
          : `Flow ${newFlowId} (Copy)`,
        createdAt: Date.now(),
        lastModified: Date.now(),
      };
      localStorage.setItem(`flow_metadata_${wabaId}_${newFlowId}`, JSON.stringify(newMetadata));
      
      // Reload flows
      loadFlows();
    } catch (e: any) {
      setError(e?.message || 'Failed to duplicate flow');
    }
  };

  const handlePreview = async (flowId: string) => {
    if (!wabaId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Refresh token before fetching flow
      console.log('Refreshing token before previewing flow...');
      const newToken = await refreshToken();
      if (!newToken) {
        console.warn('Token refresh failed or no refresh token available, proceeding with current token');
      } else {
        console.log('Token refreshed successfully');
      }
      
      // Fetch flow from API
      const response = await getFlowById(flowId);
      
      // Handle response structure: { success: true, data: { flow_settings: [...], bot_settings: {...}, assign_groups: [...] } }
      const flowData = response?.data || response;
      
      if (!flowData) {
        setError('Flow not found');
        return;
      }
      
      // Extract flow_settings array
      const flowSettings = flowData.flow_settings || [];
      
      // Set preview data
      setPreviewFlowData(flowSettings);
      setPreviewFlowId(flowId);
      setActiveTab('flow');
      
      // Load bot settings and assigned groups from API response
      if (flowData.bot_settings) {
        setBotSettings(flowData.bot_settings);
      }
      if (flowData.assign_groups) {
        setSelectedGroupIds(flowData.assign_groups);
      }
      
      // Fetch template details for all template nodes
      await fetchTemplateDetails(flowSettings);
      
      // Fetch phone groups
      fetchPhoneGroupsForPreview();
    } catch (e: any) {
      console.error('Error loading flow preview:', e);
      setError(e?.message || 'Failed to load flow preview');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateDetails = async (flowData: any[]) => {
    const templateIds = flowData
      .filter((node: any) => node.kind === 'template' && node.config?.templateId)
      .map((node: any) => node.config.templateId);
    
    const details: Record<string, any> = {};
    
    for (const templateId of templateIds) {
      try {
        const template = await getTemplate(templateId);
        details[templateId] = template;
      } catch (error) {
        console.error(`Failed to fetch template ${templateId}:`, error);
      }
    }
    
    setTemplateDetails(details);
  };

  // Replace template variables with values from node config
  const replaceTemplateVariables = (text: string, variables: Record<string, string> = {}) => {
    if (!text) return '';
    return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
      return variables[num] || match;
    });
  };

  // Render small template preview
  const renderTemplatePreview = (node: any) => {
    if (node.kind !== 'template' || !node.config?.templateId) {
      return null;
    }
    
    const template = templateDetails[node.config.templateId];
    if (!template) {
      // Fallback to simple badge while loading
      return (
        <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500 text-white">
          {node.config?.templateName || 'Template'}
        </div>
      );
    }
    
    const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
    const headerComponent = template.components?.find((c: any) => c.type === 'HEADER' && c.format === 'IMAGE');
    const footerComponent = template.components?.find((c: any) => c.type === 'FOOTER');
    
    const bodyText = bodyComponent?.text || template.content || '';
    const variables = node.config?.variables || {};
    const replacedText = replaceTemplateVariables(bodyText, variables);
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[200px]">
        {/* Header Image */}
        {headerComponent && (headerComponent.example?.header_handle?.[0] || variables['header_url']) && (
          <div className="w-full h-20">
            <img 
              src={variables['header_url'] || headerComponent.example?.header_handle?.[0]}
              alt="Template header"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Body Text */}
        <div className="p-2">
          <div className="text-xs text-gray-800 line-clamp-3 break-words">
            {replacedText || bodyText}
          </div>
        </div>
        
        {/* Footer */}
        {footerComponent?.text && (
          <div className="px-2 pb-2">
            <div className="text-xs text-gray-500 italic">
              {footerComponent.text}
            </div>
          </div>
        )}
      </div>
    );
  };

  const fetchPhoneGroupsForPreview = async () => {
    if (!wabaId) return;
    
    setGroupsLoading(true);
    try {
      const response = await getPhoneGroups(wabaId);
      const groupsData = response?.data?.groups || [];
      const transformedGroups = groupsData.map((group: any) => ({
        id: group.groupId || group.id || `group_${Date.now()}`,
        name: group.groupName || group.name || 'Unnamed Group',
        description: group.description || '',
        totalCount: group.phoneNumberCount || group.totalCount || group.phoneNumbers?.length || 0
      }));
      setPhoneGroups(transformedGroups);
    } catch (error) {
      console.error('Error fetching phone groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Build flow chain from start node with transition labels
  const buildFlowChain = (nodes: any[]) => {
    const nodeMap = new Map(nodes.map((n: any) => [n.id, n]));
    const chain: Array<{ node: any; label?: string }> = [];
    const visited = new Set<string>();
    
    // Find start node
    const startNode = nodes.find((n: any) => n.kind === 'start');
    if (!startNode) return chain;
    
    let currentNode: any = startNode;
    
    while (currentNode && !visited.has(currentNode.id)) {
      visited.add(currentNode.id);
      
      // Get transition label for the next connection
      let transitionLabel: string | undefined;
      if (currentNode.transitions) {
        const transition = Object.values(currentNode.transitions)[0] as any;
        if (transition && transition.label) {
          transitionLabel = transition.label;
        }
      }
      
      chain.push({ node: currentNode, label: transitionLabel });
      
      // Find next node from transitions
      if (currentNode.transitions) {
        const transition = Object.values(currentNode.transitions)[0] as any;
        if (transition && transition.target) {
          currentNode = nodeMap.get(transition.target);
        } else {
          break;
        }
      } else {
        break;
      }
    }
    
    return chain;
  };

  const saveFlowSettings = () => {
    if (!wabaId || !previewFlowId) return;
    
    const settingsKey = `flow_settings_${wabaId}_${previewFlowId}`;
    const settings = {
      botSettings,
      selectedGroupIds,
    };
    localStorage.setItem(settingsKey, JSON.stringify(settings));
    alert('Settings saved successfully!');
  };

  // Fetch phone numbers from selected groups and generate previews
  const generateRecipientPreviews = async () => {
    if (!previewFlowData || selectedGroupIds.length === 0) {
      setRecipientPreviews([]);
      return;
    }

    setLoadingPreviews(true);
    try {
      const allRecipients: any[] = [];

      // Fetch phone numbers from all selected groups
      for (const groupId of selectedGroupIds) {
        try {
          const response = await getGroupPhoneNumbersAll(groupId);
          
          // Extract phone numbers from response
          let phoneNumbers: any[] = [];
          if (Array.isArray(response)) {
            phoneNumbers = response;
          } else if (response?.data) {
            if (Array.isArray(response.data)) {
              phoneNumbers = response.data;
            } else if (response.data?.phoneNumbers && Array.isArray(response.data.phoneNumbers)) {
              phoneNumbers = response.data.phoneNumbers;
            }
          } else if (response?.phoneNumbers && Array.isArray(response.phoneNumbers)) {
            phoneNumbers = response.phoneNumbers;
          }

          // Transform to recipient format
          phoneNumbers.forEach((phone: any) => {
            allRecipients.push({
              id: phone.id || phone.phoneNumberId || `phone_${Date.now()}_${Math.random()}`,
              phoneNumber: phone.phoneNumber || phone.phone_number || phone.number || phone.phone || '',
              name: phone.name || phone.contactName || phone.customerName || 'Unknown',
              email: phone.email || '',
              price: phone.price || phone.amount || '',
              image: phone.image || phone.imageUrl || phone.avatar || '',
              // Add any other fields from backend
              ...phone
            });
          });
        } catch (error) {
          console.error(`Error fetching phone numbers for group ${groupId}:`, error);
        }
      }

      // Generate previews for each recipient
      const previews = allRecipients.map((recipient) => {
        // Find first template node in flow
        const templateNode = previewFlowData.find((node: any) => node.kind === 'template');
        
        if (!templateNode) {
          return {
            recipient,
            preview: null as any,
            error: 'No template found in flow'
          };
        }

        const template = templateDetails[templateNode.config?.templateId];
        if (!template) {
          return {
            recipient,
            preview: null as any,
            error: 'Template not loaded'
          };
        }

        // Map recipient data to template variables
        // This maps common fields - backend can send column names that match
        const variables: Record<string, string> = {
          '1': recipient.name || '',
          '2': recipient.price || '',
          '3': recipient.phoneNumber || '',
          '4': recipient.email || '',
          '5': recipient.location || '',
          // Add more mappings based on backend column names
          ...(templateNode.config?.variables || {})
        };

        // If backend sends specific column names, use those
        Object.keys(recipient).forEach((key) => {
          // Map common column names to variable numbers
          if (key.toLowerCase().includes('name')) {
            variables['1'] = recipient[key];
          } else if (key.toLowerCase().includes('price') || key.toLowerCase().includes('amount')) {
            variables['2'] = recipient[key];
          } else if (key.toLowerCase().includes('phone')) {
            variables['3'] = recipient[key];
          } else if (key.toLowerCase().includes('email')) {
            variables['4'] = recipient[key];
          } else if (key.toLowerCase().includes('location') || key.toLowerCase().includes('address')) {
            variables['5'] = recipient[key];
          }
        });

        return {
          recipient,
          template,
          templateNode,
          variables,
          preview: generateTemplatePreview(template, variables, recipient.image || recipient.header_url)
        };
      });

      setRecipientPreviews(previews);
    } catch (error) {
      console.error('Error generating recipient previews:', error);
      setError('Failed to generate recipient previews');
    } finally {
      setLoadingPreviews(false);
    }
  };

  // Generate template preview HTML
  const generateTemplatePreview = (template: any, variables: Record<string, string>, headerImage?: string) => {
    const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
    const headerComponent = template.components?.find((c: any) => c.type === 'HEADER' && c.format === 'IMAGE');
    const footerComponent = template.components?.find((c: any) => c.type === 'FOOTER');
    
    const bodyText = bodyComponent?.text || template.content || '';
    const replacedText = replaceTemplateVariables(bodyText, variables);
    
    return {
      bodyText: replacedText,
      headerImage: headerImage || headerComponent?.example?.header_handle?.[0] || variables['header_url'],
      footer: footerComponent?.text || '',
      hasHeader: !!headerComponent
    };
  };

  // Auto-generate previews when groups are selected
  useEffect(() => {
    if (selectedGroupIds.length > 0 && previewFlowData && Object.keys(templateDetails).length > 0) {
      generateRecipientPreviews();
    } else {
      setRecipientPreviews([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupIds, previewFlowData, templateDetails]);

  if (!wabaId) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            Error: WABA ID not found. Please log in again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Page header - matches templates list style */}
        <div className="mb-4">
          <h1 className="text-xl font-semibold mb-1">Automated Flows</h1>
            <p className="text-sm text-gray-600">WABA ID: {wabaId}</p>
          </div>
        
        <div className="mb-4 flex items-center justify-end">
            <button
              onClick={handleCreate}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-700 font-medium"
            >
              Create Flow
            </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="text-left text-sm text-gray-600">
                  <th className="px-5 py-3">Flow name</th>
                  <th className="px-5 py-3">Nodes</th>
                  <th className="px-5 py-3">Connections</th>
                  <th className="px-5 py-3">Node types</th>
                  <th className="px-5 py-3">Last modified</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      {[...Array(7)].map((__, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 w-28 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                )}

                {!loading && flows.length === 0 && (
                  <tr className="border-t border-gray-100">
                    <td className="px-5 py-10 text-center text-sm text-gray-500" colSpan={7}>
                      No automated flows yet. Create your first flow to get started.
                    </td>
                  </tr>
                )}

                {!loading && flows.map((flow) => (
                  <tr key={flow.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-900">{flow.name}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {flow.nodeCount}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {flow.edgeCount}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {flow.nodeTypes && flow.nodeTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {flow.nodeTypes.slice(0, 3).map((type, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800"
                            >
                              {type}
                            </span>
                          ))}
                          {flow.nodeTypes.length > 3 && (
                            <span className="text-xs text-gray-500">+{flow.nodeTypes.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {formatDate(flow.lastModified)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700">
                      {formatDate(flow.createdAt)}
                    </td>
                    <td className="px-5 py-4">
                        <button
                        onClick={() => handlePreview(flow.id)}
                        className="rounded-md bg-purple-100 px-3 py-1.5 text-sm font-medium text-purple-700 hover:bg-purple-200"
                      >
                        Preview
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer: flow count */}
          <div className="border-t border-gray-200 px-5 py-3">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div>
                {flows.length} automated flow{flows.length !== 1 ? 's' : ''} shown
              </div>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {previewFlowId && previewFlowData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  Flow Preview: {flows.find(f => f.id === previewFlowId)?.name || 'Flow'}
                </h3>
                <button
                  onClick={() => {
                    setPreviewFlowId(null);
                    setPreviewFlowData(null);
                    setActiveTab('flow');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-4">
                  <button
                    onClick={() => setActiveTab('flow')}
                    className={`py-2 px-4 border-b-2 font-medium text-sm ${
                      activeTab === 'flow'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Flow Structure
                  </button>
                  <button
                    onClick={() => setActiveTab('bot')}
                    className={`py-2 px-4 border-b-2 font-medium text-sm ${
                      activeTab === 'bot'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Assign Bot Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('numbers')}
                    className={`py-2 px-4 border-b-2 font-medium text-sm ${
                      activeTab === 'numbers'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Assign Groups
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="space-y-4">
                {activeTab === 'flow' && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Flow Structure</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <span className="text-xs text-gray-500">Total Nodes:</span>
                          <span className="ml-2 font-medium">{previewFlowData.length}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Total Connections:</span>
                          <span className="ml-2 font-medium">
                            {previewFlowData.reduce((acc: number, node: any) => 
                              acc + (node.transitions ? Object.keys(node.transitions).length : 0), 0)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Visual Flow Chain */}
                      <div className="mb-6">
                        <h5 className="text-xs font-semibold text-gray-600 mb-3">Flow Chain:</h5>
                        <div className="flex items-center flex-wrap gap-3">
                          {buildFlowChain(previewFlowData).map((item: any, idx: number) => {
                            const node = item.node;
                            const isLast = idx === buildFlowChain(previewFlowData).length - 1;
                            
                            return (
                              <React.Fragment key={node.id}>
                                <div className="flex flex-col items-center gap-2">
                                  {node.kind === 'template' ? (
                                    renderTemplatePreview(node) || (
                                      <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500 text-white">
                                        {node.config?.templateName || 'Template'}
                                      </div>
                                    )
                                  ) : (
                                    <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                                      node.kind === 'start' ? 'bg-green-500 text-white' :
                                      node.kind === 'text' ? 'bg-purple-500 text-white' :
                                      node.kind === 'webhook' ? 'bg-yellow-500 text-white' :
                                      node.kind === 'http_request' ? 'bg-orange-500 text-white' :
                                      node.kind === 'end' ? 'bg-red-500 text-white' :
                                      'bg-gray-500 text-white'
                                    }`}>
                                      {node.kind === 'start' ? 'Start' :
                                       node.kind === 'text' ? (node.config?.textContent?.substring(0, 20) || 'Text') :
                                       node.kind === 'webhook' ? 'Webhook' :
                                       node.kind === 'http_request' ? 'HTTP' :
                                       node.kind === 'end' ? 'End' :
                                       node.kind}
                                    </div>
                                  )}
                                </div>
                                {!isLast && (
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="text-gray-400 text-lg">→</div>
                                    {item.label && (
                                      <div className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded max-w-[120px] truncate" title={item.label}>
                                        {item.label}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'bot' && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Bot Settings</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={botSettings.enabled}
                            onChange={(e) => setBotSettings({ ...botSettings, enabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Enable Bot</span>
                        </label>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bot Name
                        </label>
                        <input
                          type="text"
                          value={botSettings.name}
                          onChange={(e) => setBotSettings({ ...botSettings, name: e.target.value })}
                          placeholder="Enter bot name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={botSettings.description}
                          onChange={(e) => setBotSettings({ ...botSettings, description: e.target.value })}
                          placeholder="Enter bot description"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'numbers' && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Assign Mobile Numbers</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {groupsLoading ? (
                        <div className="text-center py-4 text-sm text-gray-500">Loading groups...</div>
                      ) : phoneGroups.length === 0 ? (
                        <div className="text-center py-4 text-sm text-gray-500">No phone groups available</div>
                      ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {phoneGroups.map((group) => (
                            <label
                              key={group.id}
                              className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedGroupIds.includes(group.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedGroupIds([...selectedGroupIds, group.id]);
                                  } else {
                                    setSelectedGroupIds(selectedGroupIds.filter(id => id !== group.id));
                                  }
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-sm text-gray-900">{group.name}</div>
                                {group.description && (
                                  <div className="text-xs text-gray-500">{group.description}</div>
                                )}
                                <div className="text-xs text-gray-500 mt-1">
                                  {group.totalCount || 0} phone number{group.totalCount !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
                <button
                  onClick={() => {
                    setPreviewFlowId(null);
                    setPreviewFlowData(null);
                    setActiveTab('flow');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setPreviewFlowId(null);
                    setPreviewFlowData(null);
                    handleEdit(previewFlowId);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit Flow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Delete Flow</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this flow? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  disabled={deletingFlowId === showDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingFlowId === showDeleteConfirm ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

